import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFabStackSlot } from '../../contexts/FabStackContext';
import { FAB_PRIORITY } from '../../lib/fabStack';
import {
  captureScreenshot,
  collectContext,
  installConsoleErrorCapture,
  submitBugReport,
  enqueuePendingBugReport,
  newClientMessageId,
  flushPendingBugReports,
  isNetworkFailure
} from '../../services/bugReporter';
import './BugReporter.css';

const PANEL_WIDTH = 380;

const DISCLAIMER = `By submitting, you confirm the description does not contain sensitive info (passwords, personal details of third parties). We capture your screenshot, current page URL, and browser info to help diagnose the issue. Reports are sent to the Tacit team.`;

const BUG_REPORT_ICON = <span aria-hidden="true">🐞</span>;

/**
 * Floating bug/suggestion reporter (Issue #887).
 *
 * Always eligible for authenticated, non-guest users (not gated by
 * VITE_TESTING_PANEL_ENABLED). Desktop: right-side panel that pushes content.
 * Mobile: full-screen sheet.
 *
 * #1696 (2026-07-27 DECIDED): no longer renders its own fixed-position FAB.
 * Registers 'bugReport' as a 'menu'-corner FabStack entry — the shared
 * FabMenu (components/fabMenu/FabMenu.jsx) renders the trigger and calls back
 * into `openPanel` on tap. The panel itself (below) is unchanged. The
 * first-login coach-mark popover (#1066) that used to anchor to this FAB's
 * own fixed position is simplified to a badge dot on the shared FabMenu
 * trigger (`badge: showIntro` in the registered meta) — a full popover
 * anchored to a specific screen position no longer makes sense once the FAB
 * itself is folded into a shared menu that can hold any number of items in
 * any order; `dismissIntro()` still retires it (once, per user) the first
 * time the item is opened.
 */
export default function BugReporter() {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [screenshotData, setScreenshotData] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Issue #2611: distinct from `submitted` — the report reached the server
  // vs. it was saved locally because the POST failed on a network error and
  // will be retried. Kept separate so the two states can carry different
  // copy (see the panel body below) rather than the queued case lying about
  // having reached the team.
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState(null);
  const [showIntro, setShowIntro] = useState(false);

  const eligible = isAuthenticated && !user?.is_guest;
  const location = useLocation();
  const isChatRoute = location.pathname.startsWith('/chat/');

  useEffect(() => {
    if (eligible) installConsoleErrorCapture();
  }, [eligible]);

  // Issue #2611: flush any bug reports queued from a prior session/tab that
  // failed to submit over a network error. "Flush on next app load" per the
  // issue — this effect runs once per mount, and <BugReporter/> is a
  // singleton mounted at the app root that never remounts on navigation, so
  // this fires once per app load for an eligible (signed-in, non-guest)
  // learner. Also retried opportunistically on `online` so a learner who
  // stays on the same page doesn't have to reload to have a saved report
  // actually sent once their connection returns.
  useEffect(() => {
    if (!eligible) return undefined;
    // Owner-scoped: only this user's own queued reports are sent. See
    // flushPendingBugReports — the queue is per-browser and survives sign-out.
    const ownerId = user?.id ?? null;
    flushPendingBugReports(ownerId);
    const onOnline = () => { flushPendingBugReports(ownerId); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [eligible, user?.id]);

  // First-login coach-mark for the bug-reporter FAB (#1066). Shown once per
  // user, persisted in localStorage. Suppressed on /chat (the FAB repositions
  // there and the popover would mis-anchor; first login lands on the
  // dashboard anyway). <BugReporter /> is a singleton mounted at the app root
  // (App.jsx) and never remounts on navigation, so the chat-route case must
  // actively *clear* showIntro — early-returning would leave a stale,
  // mis-anchored coach-mark (+ FAB pulse) pinned to /chat if the learner
  // entered a scenario without dismissing it. Not persisted: it reappears
  // next time the learner is on a non-chat route and still hasn't seen it,
  // preserving the show-once intent.
  useEffect(() => {
    if (!eligible || !user?.id) return;
    if (isChatRoute) {
      setShowIntro(false);
      return;
    }
    try {
      if (!localStorage.getItem(`bugReporterIntroSeen:${user.id}`)) {
        setShowIntro(true);
      }
    } catch (_e) {
      /* localStorage unavailable (private mode) — skip the intro silently. */
    }
  }, [eligible, isChatRoute, user?.id]);

  const dismissIntro = useCallback(() => {
    setShowIntro(false);
    try {
      if (user?.id) localStorage.setItem(`bugReporterIntroSeen:${user.id}`, '1');
    } catch (_e) {
      /* Best-effort; if it fails the coach-mark reappears next session. */
    }
  }, [user?.id]);

  // Push content left when open (desktop only — CSS media query handles)
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--bug-reporter-width',
      open ? `${PANEL_WIDTH}px` : '0px'
    );
    return () => document.documentElement.style.setProperty('--bug-reporter-width', '0px');
  }, [open]);

  // Auto-close after successful submit OR after the report was saved locally
  // for retry (Issue #2611) — either way the learner's note is off their
  // hands and the panel shouldn't linger.
  useEffect(() => {
    if (!submitted && !queued) return undefined;
    const t = setTimeout(() => {
      setSubmitted(false);
      setQueued(false);
      setOpen(false);
      setNote('');
      setScreenshotData(null);
    }, 2000);
    return () => clearTimeout(t);
  }, [submitted, queued]);

  const handleCapture = useCallback(async () => {
    setCapturing(true);
    setError(null);
    try {
      // Briefly hide the panel before capture so it doesn't appear in the shot.
      // html2canvas-ignore attribute is respected in captureScreenshot.
      const data = await captureScreenshot();
      if (!data) {
        setError('Screenshot capture failed — you can still submit without one.');
      } else {
        setScreenshotData(data);
      }
    } finally {
      setCapturing(false);
    }
  }, []);

  const handleRetake = useCallback(() => {
    setScreenshotData(null);
    handleCapture();
  }, [handleCapture]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    const trimmed = note.trim();
    if (trimmed.length < 1) {
      setError('Please describe the bug or suggestion.');
      return;
    }
    if (trimmed.length > 2000) {
      setError('Description is too long (max 2000 characters).');
      return;
    }
    setSubmitting(true);
    setError(null);
    const ctx = collectContext(user);
    // Mint the idempotency key BEFORE the first submit. Migration 0409's unique
    // index is partial (WHERE client_message_id IS NOT NULL), so a first row
    // written without one can never be matched by the retry's dedup lookup —
    // the retry would insert a SECOND row and re-fire the notification email
    // and GitHub issue, which is the exact lost-response case #2611 exists to
    // prevent (mega100 review-fix).
    const clientMessageId = newClientMessageId();
    const payload = {
      note: trimmed,
      url: ctx.url,
      route: ctx.route,
      userAgent: ctx.userAgent,
      metadata: ctx.metadata,
      screenshot: screenshotData || null,
      clientMessageId
    };
    try {
      await submitBugReport(payload);
      setSubmitted(true);
    } catch (err) {
      // Issue #2611: a network failure (safeFetch's FetchError{status:0} —
      // the POST never reached the server, or its response never reached
      // us) used to surface as a raw "HTTP 0 Network error on POST
      // /api/bug-reports" and the typed report was gone — no row, nothing
      // queued. Persist it for retry instead of showing that transport-level
      // text to the learner. A real 4xx/5xx (bad input, rate limit, server
      // error) is still shown as before — those are cases where retrying the
      // exact same request silently would be wrong.
      if (isNetworkFailure(err)) {
        enqueuePendingBugReport(payload, clientMessageId, user?.id ?? null);
        setQueued(true);
      } else {
        const msg = err?.message || 'Failed to submit — please try again.';
        setError(msg.includes('429') ? 'You\'ve hit the rate limit — wait a minute and try again.' : msg);
      }
    } finally {
      setSubmitting(false);
    }
  }, [note, screenshotData, submitting, user]);

  // review-fix H_fabstack_1696 (P1): memoized so this identity is stable
  // across re-renders that don't actually change what tapping the item
  // should do (e.g. every keystroke in the note textarea while the panel is
  // open) — only recomputes when `showIntro` or `dismissIntro` itself
  // changes. FabStackContext's useFabStackSlot now re-runs register() on
  // EVERY render while active (see that file's "freshness effect"), relying
  // entirely on register()'s shallow-equality bail-out to avoid a real state
  // update when nothing changed — an UNmemoized inline arrow here would get
  // a new reference on every BugReporter render, defeat that bail-out, and
  // loop renders.
  const handleTriggerActivate = useCallback(() => {
    setOpen(true);
    if (showIntro) dismissIntro();
  }, [showIntro, dismissIntro]);

  // #1696: register 'bugReport' in the shared 'menu'-corner FabStack — this
  // hook must run every render (React rules of hooks), so it sits ahead of
  // the `!eligible` early return, reporting `eligible` as `active` like
  // every other menu registrant.
  useFabStackSlot('menu', 'bugReport', FAB_PRIORITY.BUG_REPORT, eligible, {
    icon: BUG_REPORT_ICON,
    label: 'Report',
    ariaLabel: 'Report a bug or send a suggestion',
    onClick: handleTriggerActivate,
    testId: 'fab-menu-item-bug-report',
    badge: showIntro,
  });

  if (!eligible) return null;
  if (!open) return null;

  return (
    <div
      className="bug-reporter-panel"
      role="dialog"
      aria-label="Bug reporter"
      aria-modal="true"
      data-html2canvas-ignore="true"
    >
      <header className="bug-reporter-panel__header">
        <h3 className="bug-reporter-panel__title">Report a bug or suggestion</h3>
        <button
          type="button"
          className="bug-reporter-panel__close"
          onClick={() => setOpen(false)}
          aria-label="Close bug reporter"
        >
          ×
        </button>
      </header>

      <div className="bug-reporter-panel__body">
        {submitted ? (
          <div className="bug-reporter-panel__success">
            <h4>Thanks — we got it!</h4>
            <p>The team will take a look. This panel will close shortly.</p>
          </div>
        ) : queued ? (
          // Issue #2611: the POST failed on a network error, so this did NOT
          // reach the server — it's saved on this device and will send
          // automatically once the connection is back (retried on `online`
          // and on next app load; see flushPendingBugReports). Deliberately
          // says nothing the network didn't confirm, and names no product —
          // avoids the branding gap a hardcoded "Tacit"/team name would open
          // for an org with a different app_name (e.g. Umuzi's "Praxis").
          <div className="bug-reporter-panel__success">
            <h4>Saved — we'll send it when you're back online.</h4>
            <p>Your connection dropped, so we couldn't send this right away. It's saved on this device and will go through automatically once you're back online.</p>
          </div>
        ) : (
          <>
            <label htmlFor="bug-reporter-note" className="bug-reporter-panel__label">
              What went wrong — or what would help?
            </label>
            <textarea
              id="bug-reporter-note"
              className="bug-reporter-panel__textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. The Submit button on the assignment page didn't respond when I clicked it. I was trying to finish scenario X."
              maxLength={2000}
              rows={6}
              autoFocus
            />
            <div className="bug-reporter-panel__charcount">
              {note.length}/2000
            </div>

            <div className="bug-reporter-panel__screenshot">
              <div className="bug-reporter-panel__screenshot-header">
                <span>Screenshot</span>
                {screenshotData ? (
                  <button
                    type="button"
                    className="bug-reporter-panel__link-btn"
                    onClick={handleRetake}
                    disabled={capturing}
                  >
                    Retake
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bug-reporter-panel__link-btn"
                    onClick={handleCapture}
                    disabled={capturing}
                  >
                    {capturing ? 'Capturing…' : 'Capture'}
                  </button>
                )}
              </div>
              {screenshotData ? (
                <img
                  className="bug-reporter-panel__screenshot-img"
                  src={screenshotData}
                  alt="Screenshot preview"
                />
              ) : (
                <div className="bug-reporter-panel__screenshot-placeholder">
                  {capturing ? 'Capturing…' : 'No screenshot (optional).'}
                </div>
              )}
            </div>

            <p className="bug-reporter-panel__consent">
              {DISCLAIMER}
            </p>

            {error && (
              <div className="bug-reporter-panel__error" role="alert">{error}</div>
            )}

            <div className="bug-reporter-panel__actions">
              <button
                type="button"
                className="bug-reporter-panel__btn bug-reporter-panel__btn--secondary"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bug-reporter-panel__btn bug-reporter-panel__btn--primary"
                onClick={handleSubmit}
                disabled={submitting || !note.trim()}
              >
                {submitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
